import React, { useCallback, useEffect, useId, useState } from "react";
import { ArrowUpRight, CircleCheck, CircleX, PackagePlus, RotateCcw, Truck, Warehouse } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import { Notice, Section } from "./FieldPrimitives";
import {
  Ear,
  NewItem,
  StockItem,
  StockKind,
  StockPage,
  STATE_WORDS,
  KIND_WORDS,
  deviceLine,
  providerStockApi,
  shortDay,
  unanswered,
  useActionKeys,
} from "./providerStockApi";
import "./field-stock.css";

/**
 * "My stock" (Isaiah Cotto, 2026-10-22, after provider Renata Silva described her car as a mobile office). The
 * provider's own stock: hearing aids by serial, accessories and tools by count. Add, adjust a count, mark lost or
 * returned, and release a device loaded for a visit. Only the provider changes their own stock (the server decides).
 * Opening stock comes once from the provider stock sheets; the configured workbook stays as a secondary link.
 *
 * Status is said in words and a shape, never colour alone. Mobile first: one column, large targets.
 */
export interface MyStockProps {
  stockReportUrl?: string | null;
  offline?: boolean;
}

type Load =
  | { state: "loading" }
  | { state: "ready"; page: StockPage }
  | { state: "refused"; message: string }
  | { state: "error"; message: string };

const NOT_REACHED = "Could not reach eMerge to load your stock.";
const STALE = "This item changed since your list loaded (another tab or device). Your stock was reloaded; check it and try again.";

function StateTag({ item }: { item: StockItem }) {
  const Icon = item.state === "IN_STOCK" ? CircleCheck : item.state === "ASSIGNED" ? Truck : CircleX;
  return (
    <span className={`fw-stock-tag ${item.state.toLowerCase()}`}>
      <Icon size={14} aria-hidden="true" />
      {STATE_WORDS[item.state]}
    </span>
  );
}

function refusal(r: { status?: number; code?: string; message?: string }): Load {
  if (unanswered(r)) return { state: "error", message: NOT_REACHED };
  if (r.code === "PROVIDER_STOCK_NOT_INSTALLED") return { state: "refused", message: "Provider stock is not switched on yet." };
  if (r.status === 403) return { state: "refused", message: r.message || "Only a provider sees their own stock here." };
  return { state: "error", message: r.message || "Your stock could not be loaded." };
}

function AddItemForm({ disabled, onAdded }: { disabled: boolean; onAdded: (message: string) => void }) {
  const ids = useId();
  const [kind, setKind] = useState<StockKind>("HEARING_AID");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [ear, setEar] = useState<Ear | "">("");
  const [qty, setQty] = useState("1");
  const { keyFor, settle } = useActionKeys();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const serialRequired = kind === "HEARING_AID";
  const qtyNumber = Number(qty.trim());
  const qtyOk = serialRequired || serial.trim() !== "" || (/^[0-9]{1,5}$/.test(qty.trim()) && qtyNumber <= 10000);
  const ready = !disabled && !saving && (!serialRequired || serial.trim() !== "") && qtyOk;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setSaving(true);
    setError("");
    const item: NewItem = { kind, model: model.trim() || undefined, serial: serial.trim() || undefined, ear };
    if (!serialRequired && !serial.trim()) item.qty = qtyNumber;
    try {
      // The key belongs to exactly what is being added: editing the form after a lost answer is a new add.
      const action = JSON.stringify(item);
      const r = await providerStockApi.add(item, keyFor(action));
      if (r.success && r.data) {
        setModel("");
        setSerial("");
        setEar("");
        setQty("1");
        settle(action);
        onAdded(`${KIND_WORDS[kind]} added to your stock.`);
      } else if (unanswered(r)) {
        setError("eMerge did not confirm the item. Nothing is shown as added. Try again; it will not be added twice.");
      } else {
        setError(r.message || r.error || "The item was not added.");
        settle(action);
      }
    } catch {
      setError("eMerge did not confirm the item. Nothing is shown as added. Try again; it will not be added twice.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="fw-stock-form" onSubmit={submit} aria-labelledby={`${ids}-title`}>
      <h3 id={`${ids}-title`} className="fw-stock-subtitle">
        <PackagePlus size={16} aria-hidden="true" /> Add to my stock
      </h3>
      <label htmlFor={`${ids}-kind`}>What it is</label>
      <select id={`${ids}-kind`} value={kind} onChange={(e) => setKind(e.target.value as StockKind)} disabled={saving}>
        <option value="HEARING_AID">Hearing aid</option>
        <option value="ACCESSORY">Accessory (domes, batteries, chargers…)</option>
        <option value="TOOL">Tool</option>
      </select>
      <label htmlFor={`${ids}-serial`}>Serial number{serialRequired ? " (required)" : " (optional)"}</label>
      <input
        id={`${ids}-serial`}
        value={serial}
        onChange={(e) => setSerial(e.target.value)}
        required={serialRequired}
        maxLength={60}
        autoComplete="off"
        autoCapitalize="characters"
        readOnly={saving}
      />
      <label htmlFor={`${ids}-model`}>Model (optional)</label>
      <input id={`${ids}-model`} value={model} onChange={(e) => setModel(e.target.value)} maxLength={80} readOnly={saving} />
      {kind === "HEARING_AID" && (
        <>
          <label htmlFor={`${ids}-ear`}>Ear (optional)</label>
          <select id={`${ids}-ear`} value={ear} onChange={(e) => setEar(e.target.value as Ear | "")} disabled={saving}>
            <option value="">Not set</option>
            <option value="LEFT">Left</option>
            <option value="RIGHT">Right</option>
          </select>
        </>
      )}
      {!serialRequired && !serial.trim() && (
        <>
          <label htmlFor={`${ids}-qty`}>How many</label>
          <input
            id={`${ids}-qty`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            aria-invalid={!qtyOk}
            readOnly={saving}
          />
        </>
      )}
      {error && <p role="alert" className="fw-error">{error}</p>}
      <div className="fw-actions">
        <Button className="fw-primary" type="submit" disabled={!ready}>
          {saving ? "Adding…" : "Add to my stock"}
        </Button>
      </div>
    </form>
  );
}

function ItemRow({
  item,
  disabled,
  onChanged,
}: {
  item: StockItem;
  disabled: boolean;
  onChanged: (message: string, reload: boolean) => void;
}) {
  const ids = useId();
  const [qty, setQty] = useState(String(item.qty));
  const [confirm, setConfirm] = useState<null | "LOST" | "RETURNED">(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { keyFor, settle } = useActionKeys();
  // A new count from the server (after a save or a reload) replaces the box; typing does not get overwritten.
  const [seenQty, setSeenQty] = useState(item.qty);
  if (seenQty !== item.qty) {
    setSeenQty(item.qty);
    setQty(String(item.qty));
  }

  const run = async (action: string, call: (k: string) => Promise<{ success: boolean; status?: number; code?: string; message?: string; error?: string }>, done: string) => {
    setBusy(true);
    setError("");
    try {
      const r = await call(keyFor(action));
      if (r.success) {
        settle(action);
        setConfirm(null);
        onChanged(done, true);
      } else if (unanswered(r)) {
        setError("eMerge did not confirm the change. Try again; it will not be applied twice.");
      } else if (r.code === "STALE_VERSION") {
        settle(action);
        onChanged(STALE, true);
      } else {
        settle(action);
        setError(r.message || r.error || "The change was not saved.");
      }
    } catch {
      setError("eMerge did not confirm the change. Try again; it will not be applied twice.");
    } finally {
      setBusy(false);
    }
  };

  const countable = item.kind !== "HEARING_AID" && !item.serial && item.state === "IN_STOCK";
  const qtyNumber = Number(qty.trim());
  const qtyOk = /^[0-9]{1,5}$/.test(qty.trim()) && qtyNumber <= 10000 && qtyNumber !== item.qty;
  const label = deviceLine(item);
  const lf = item.loaded_for;

  return (
    <li className="fw-stock-item">
      <div className="fw-stock-item-head">
        <strong>{label}</strong>
        <StateTag item={item} />
      </div>
      {item.kind !== "HEARING_AID" && !item.serial && <p className="fw-muted">Count: {item.qty}</p>}
      {item.origin === "SHEET_IMPORT" && <p className="fw-muted">From your stock sheet. Check it is still with you.</p>}
      {lf && (
        <p className={lf.plan_open ? "fw-muted" : "fw-stock-warn"}>
          {lf.plan_open ? "For" : "Still loaded for a past or cancelled visit:"} {shortDay(lf.planned_on)} at{" "}
          {lf.facility.name || "a building"}. Loading is a plan; it is not a dispense.
        </p>
      )}
      {error && <p role="alert" className="fw-error">{error}</p>}
      <div className="fw-stock-actions">
        {countable && (
          <div className="fw-stock-qty">
            <label htmlFor={`${ids}-qty`}>Count for {label}</label>
            <input
              id={`${ids}-qty`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={5}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              readOnly={busy}
            />
            <Button
              variant="outline"
              type="button"
              disabled={disabled || busy || !qtyOk}
              onClick={() => run(`count:${qtyNumber}`, (k) => providerStockApi.setQuantity(item.id, item.version, qtyNumber, k), "Count saved.")}
            >
              Save count
            </Button>
          </div>
        )}
        {item.state === "ASSIGNED" && (
          <Button
            variant="outline"
            type="button"
            disabled={disabled || busy}
            onClick={() => run("release", (k) => providerStockApi.release(item.id, item.version, k), "Released back to your stock.")}
          >
            <RotateCcw size={14} aria-hidden="true" /> Release back to stock
          </Button>
        )}
        {item.state === "IN_STOCK" && !confirm && (
          <>
            <Button variant="outline" type="button" disabled={disabled || busy} onClick={() => setConfirm("LOST")}>
              Mark lost
            </Button>
            <Button variant="outline" type="button" disabled={disabled || busy} onClick={() => setConfirm("RETURNED")}>
              Mark returned
            </Button>
          </>
        )}
        {confirm && (
          <div role="group" aria-label={`Confirm: mark ${label} ${confirm === "LOST" ? "lost" : "returned"}`} className="fw-stock-confirm">
            <p>
              Mark this {confirm === "LOST" ? "lost" : "returned"}? It leaves your stock and cannot be undone here.
            </p>
            <Button
              className="fw-primary"
              type="button"
              disabled={disabled || busy}
              onClick={() => run(`mark:${confirm}`, (k) => providerStockApi.mark(item.id, item.version, confirm, k), confirm === "LOST" ? "Marked lost." : "Marked returned.")}
            >
              {confirm === "LOST" ? "Yes, mark lost" : "Yes, mark returned"}
            </Button>
            <Button variant="ghost" type="button" disabled={busy} onClick={() => setConfirm(null)}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

export default function MyStock({ stockReportUrl, offline = false }: MyStockProps) {
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [status, setStatus] = useState("");

  const fetchStock = useCallback(async () => {
    try {
      const r = await providerStockApi.list();
      if (r.success && r.data && Array.isArray(r.data.items)) setLoad({ state: "ready", page: r.data });
      else if (r.success) setLoad({ state: "error", message: "Your stock could not be loaded." });
      else setLoad(refusal(r));
    } catch {
      setLoad({ state: "error", message: NOT_REACHED });
    }
  }, []);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  const changed = (message: string, reload: boolean) => {
    setStatus(message);
    if (reload) fetchStock();
  };

  const page = load.state === "ready" ? load.page : null;
  const inStock = page ? page.items.filter((i) => i.state === "IN_STOCK") : [];
  const loaded = page ? page.items.filter((i) => i.state === "ASSIGNED") : [];
  const other = page ? page.items.filter((i) => i.state === "DISPENSED") : [];
  const disabled = offline;

  return (
    <Section title="My stock" subtitle="What you carry: hearing aids by serial number, accessories and tools by count.">
      <div className="fw-panel-content fw-stock">
        {offline && <Notice>Offline: showing the last loaded stock. Changes are unavailable until you reconnect.</Notice>}
        {load.state === "loading" && <p role="status" className="fw-muted">Loading your stock…</p>}
        {load.state === "refused" && <p className="fw-muted">{load.message}</p>}
        {load.state === "error" && (
          <div role="status" className="fw-error">
            <p>{load.message}</p>
            <Button variant="outline" type="button" onClick={fetchStock}>Try again</Button>
          </div>
        )}
        {page && !page.linked && (
          <div className="fw-empty">
            <Warehouse aria-hidden="true" />
            <h3>No provider record is linked to your login</h3>
            <p>{page.message || "Scheduling links a provider's login to their record."}</p>
          </div>
        )}
        {page && page.linked && (
          <>
            <p role="status" className="fw-save-status">{status}</p>
            {page.counts && (
              <ul className="fw-stock-counts" aria-label="Stock summary">
                <li><strong>{page.counts.hearing_aids_in_stock}</strong> hearing aids in stock</li>
                <li><strong>{page.counts.loaded}</strong> loaded for visits</li>
                <li><strong>{page.counts.accessories_and_tools}</strong> accessories and tools</li>
              </ul>
            )}
            {!!page.counts?.loaded_for_past_visits && (
              <Notice>
                {page.counts.loaded_for_past_visits} device(s) are still marked loaded for a visit that has passed or was
                cancelled. If they are back in your car, release them to stock. Nothing is dispensed from here.
              </Notice>
            )}
            <AddItemForm disabled={disabled} onAdded={(m) => changed(m, true)} />

            <h3 className="fw-stock-subtitle">Loaded for a visit ({loaded.length})</h3>
            {loaded.length === 0 ? (
              <p className="fw-muted">Nothing is loaded. Load devices for a visit's residents under Device readiness.</p>
            ) : (
              <ul className="fw-stock-list" aria-label="Loaded for a visit">
                {loaded.map((i) => <ItemRow key={i.id} item={i} disabled={disabled} onChanged={changed} />)}
              </ul>
            )}

            <h3 className="fw-stock-subtitle">In stock ({inStock.length})</h3>
            {inStock.length === 0 ? (
              <p className="fw-muted">Nothing in stock yet. Add what you carry above.</p>
            ) : (
              <ul className="fw-stock-list" aria-label="In stock">
                {inStock.map((i) => <ItemRow key={i.id} item={i} disabled={disabled} onChanged={changed} />)}
              </ul>
            )}
            {other.length > 0 && (
              <ul className="fw-stock-list" aria-label="Dispensed">
                {other.map((i) => <ItemRow key={i.id} item={i} disabled={disabled} onChanged={changed} />)}
              </ul>
            )}
            {page.closed.length > 0 && (
              <details className="fw-stock-closed">
                <summary>Returned or lost ({page.closed.length}{page.closed_truncated ? "+" : ""})</summary>
                <ul className="fw-stock-list" aria-label="Returned or lost">
                  {page.closed.map((i) => (
                    <li key={i.id} className="fw-stock-item">
                      <div className="fw-stock-item-head">
                        <span>{deviceLine(i)}</span>
                        <StateTag item={i} />
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
        {stockReportUrl && (
          <a className="fw-resource" href={stockReportUrl} target="_blank" rel="noopener noreferrer">
            Open stock report <ArrowUpRight aria-hidden="true" />
          </a>
        )}
        <Notice>
          Stock here is custody and planning only: no dispense, commission or payout is recorded or inferred.
        </Notice>
      </div>
    </Section>
  );
}
